import { useRef } from "react";
import { PAGE_X, PAGE_Y } from "../_shared/safeArea";
import { useTrackedScreen } from "../../hooks/useTrackedScreen";
import type { SchemeTokens } from "@shared/types/foyer";
import { aureliaManifest } from "@shared/schemes/manifests";
import { SchemeProvider } from "../_shared/schemeContext";
import { useScreenFocus } from "../_shared/useFocusGrid";
import { useBackHome } from "../_shared/useBackHome";
import { LauncherIcon } from "../_shared/launcher/icons";
import {
  LauncherPage,
  LauncherCustom,
  LauncherTv,
  useHomeNav,
  useLauncherClock,
  visibleFunctions,
  homeBgLayers,
  greeting,
  useGuestName,
  welcomeLine,
  SchemeSlot,
  launcherSlotStyle,
  type LauncherTheme,
  type LauncherScreen,
} from "../_shared/launcher/kit";
import { useNow } from "../_shared/useNow";
import { offerSlotBanner } from "../_shared/slots/offerSlot";
import type { SchemeProps } from "../registry";

/**
 * Aurelia - grand timeless luxury. ARCHETYPE: full-bleed property photo, light
 * top chrome, a centered floating bottom dock. The refined evolution of the
 * classic launcher. Dark espresso + brass, Cormorant Garamond.
 */
function aureliaTheme(t: SchemeTokens): LauncherTheme & { rootBg: string } {
  const e = { ...aureliaManifest.defaultTokens.extras, ...t.extras };
  return {
    serif: t.fonts.serif ?? "",
    sans: t.fonts.sans ?? "",
    surface: e.surface,
    card: e.card,
    chip: e.chip,
    line: t.palette.rule ?? "",
    ink: t.palette.ink,
    muted: t.palette.inkDim ?? "",
    accent: t.palette.accent,
    onAccent: e.onAccent,
    phTone: e.phTone,
    phInk: e.phInk,
    photoTone: "dark",
    rootBg: t.palette.bg,
  };
}

export default function AureliaScheme({ scheme }: SchemeProps) {
  const scopeRef = useRef<HTMLDivElement>(null);
  useScreenFocus(scopeRef);
  const [screen, setScreen] = useTrackedScreen<LauncherScreen>("home");
  useBackHome(() => setScreen("home"));
  const T = aureliaTheme(scheme.tokens);
  const c = useLauncherClock();
  const guest = useGuestName();
  const { hotelName, city } = scheme.content.branding;
  const hero = scheme.content.images.hero;
  const bgL = homeBgLayers(scheme.content.background?.mode);
  const funcs = visibleFunctions(scheme);
  // The home promo slot, when it holds a live offer (`POS-4`). Resolved here
  // because the dock's focus chain needs to know whether there is a cell to
  // add, and `SchemeSlot` needs to know whether to draw a ring on it - one
  // answer behind both, or the remote and the picture disagree.
  // Read off the scheme directly rather than through `useOfferSlot`: this
  // component body sits OUTSIDE its own `SchemeProvider` (the provider is in
  // the tree it returns), so the context hook has nothing to read here.
  const promoClock = useNow();
  const promoBanner = offerSlotBanner(scheme, "homePromo", promoClock);
  const home = useHomeNav({
    funcs,
    screen,
    go: setScreen,
    scopeRef,
    tvPlayer: scheme.context.tvPlayer,
    headerFirst: true,
    // Last in the chain, and only while there is a live offer: an offer nobody
    // asked for may interrupt the eye, never the remote, so the ring still
    // starts on the dock.
    promo: {
      active: !!promoBanner,
      onPress: () => setScreen("offers"),
    },
  });
  const isHome = screen === "home";
  const cream = T.ink;

  return (
    <SchemeProvider value={scheme}>
      <div
        ref={scopeRef}
        style={{
          position: "absolute",
          inset: 0,
          background: T.rootBg,
          color: cream,
          overflow: "hidden",
          fontFamily: T.sans,
          outline: "none",
        }}
      >
        {isHome ? (
          <>
            {bgL.photo && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: hero ? "#000" : T.phTone,
                  backgroundImage: hero
                    ? `url(${hero})`
                    : `repeating-linear-gradient(135deg, ${T.phInk} 0 1px, transparent 1px 11px)`,
                  backgroundSize: hero ? "cover" : undefined,
                  backgroundPosition: "center",
                }}
              />
            )}
            {bgL.tint && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(13,9,6,0.66) 0%, rgba(13,9,6,0.08) 26%, rgba(13,9,6,0.05) 60%, rgba(13,9,6,0.82) 100%)",
                }}
              />
            )}
          </>
        ) : (
          <div
            style={{ position: "absolute", inset: 0, background: T.surface }}
          />
        )}

        {/* top chrome */}
        <div
          style={{
            position: "absolute",
            top: PAGE_Y,
            left: PAGE_X,
            right: PAGE_X,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            zIndex: 2,
          }}
        >
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            {home.headerIcons.map((name, k) => {
              const on = isHome && home.headerIdx === k;
              return (
                <div
                  key={name}
                  style={{
                    padding: 8,
                    borderRadius: 10,
                    display: "flex",
                    background: on ? `${T.accent}26` : "transparent",
                    border: on
                      ? `1px solid ${T.accent}`
                      : "1px solid transparent",
                  }}
                >
                  <LauncherIcon
                    name={name}
                    size={26}
                    color={on ? T.accent : cream}
                  />
                </div>
              );
            })}
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontFamily: T.serif,
                fontSize: 40,
                letterSpacing: "0.22em",
                paddingLeft: "0.22em",
                lineHeight: 1,
                textTransform: "uppercase",
              }}
            >
              {hotelName}
            </div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.34em",
                textTransform: "uppercase",
                color: T.accent,
                marginTop: 7,
              }}
            >
              {city ? `${city} · ` : ""}Grand Hotel &amp; Spa
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <LauncherIcon name="cloud" size={26} color={cream} />
            <span style={{ fontFamily: T.serif, fontSize: 34 }}>15°</span>
            <span style={{ width: 1, height: 26, background: T.muted }} />
            <span style={{ fontFamily: T.serif, fontSize: 34 }}>
              {c.time24}
            </span>
          </div>
        </div>

        {/* body */}
        {isHome ? (
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 232,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 13,
                letterSpacing: "0.34em",
                textTransform: "uppercase",
                color: T.accent,
                marginBottom: 10,
              }}
            >
              {welcomeLine(greeting(), guest)}
            </div>
            <div
              style={{
                fontFamily: T.serif,
                fontSize: 40,
                fontWeight: 500,
                color: cream,
              }}
            >
              Welcome to your stay
            </div>
          </div>
        ) : screen === "tv" ? (
          // Full-bleed, before LauncherPage: the built-in player carries its
          // own left-hand menu and must own the whole panel, exactly like the
          // vendor app it replaces.
          <LauncherTv theme={T} go={setScreen} scopeRef={scopeRef} />
        ) : screen === "custom" ? (
          <LauncherCustom theme={T} go={setScreen} scopeRef={scopeRef} />
        ) : (
          <div
            style={{
              position: "absolute",
              // Start a touch higher + extra bottom room so the focusable Back
              // button is already in view - otherwise stepping to it nudges the
              // whole page up ~10px as it scrolls into view.
              top: 128,
              left: PAGE_X,
              right: PAGE_X,
              bottom: 32,
            }}
          >
            <LauncherPage
              screen={screen}
              theme={T}
              go={setScreen}
              scopeRef={scopeRef}
            />
          </div>
        )}

        {/* operator promo slot - top-right, under the header. It used to sit
            bottom-left, but the centered dock grows with the number of
            functions and, with six or so, runs right under it: the promo was
            covered by the very menu it is meant to sit beside. The top-right
            corner is free on the home screen (Nord, Marea and Terra put it
            there too). */}
        {isHome && (
          <SchemeSlot
            focused={home.promoFocused}
            onPress={() => setScreen("offers")}
            slotId="homePromo"
            style={launcherSlotStyle(T)}
            containerStyle={{
              position: "absolute",
              top: 160,
              right: PAGE_X,
              width: 430,
              zIndex: 2,
            }}
          />
        )}

        {/* centered bottom dock - home only */}
        {isHome && (
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 70,
              display: "flex",
              justifyContent: "center",
              zIndex: 2,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 10,
                padding: 16,
                borderRadius: 22,
                background: "rgba(18,13,9,0.62)",
                backdropFilter: "blur(12px)",
                border: `1px solid ${T.accent}33`,
              }}
            >
              {funcs.map((f, i) => {
                const on = home.funcIdx === i;
                return (
                  <div
                    key={f.key}
                    style={{
                      width: 168,
                      padding: "22px 12px 18px",
                      borderRadius: 16,
                      textAlign: "center",
                      background: on ? `${T.accent}26` : "transparent",
                      border: on
                        ? `1px solid ${T.accent}`
                        : "1px solid transparent",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginBottom: 12,
                      }}
                    >
                      <LauncherIcon
                        name={f.icon}
                        size={36}
                        color={on ? T.accent : cream}
                        stroke={1.6}
                      />
                    </div>
                    <div
                      style={{
                        fontFamily: T.sans,
                        fontSize: 16,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: on ? T.accent : cream,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {f.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </SchemeProvider>
  );
}
